/**
 * CompAdminUserCreation.jsx
 * data-fit LLC — DecisionFlow
 * Stage 2 CONFIGURE — CONF-03: Compensation administrator user creation
 * Branch: feature/compliance-lifecycle-foundation
 * Spec authority: ComponentSpec_CONFIGURE.md (S82) + MasterContext_S88_Close.md
 * Path: ui/src/components/tenant-admin/CompAdminUserCreation.jsx
 *
 * S89 audit: CLEAN — no patches required.
 * Confirmed correct:
 * - Hard seat gate: seatUsed >= seatLimit disables invite ✓
 * - OI-TENANTADMIN-STAGE12 stubs on all three endpoints ✓
 * - Load fail → inline error with retry ✓
 * - Invite fail → inline form error ✓
 * - Toast auto-dismiss 3000ms ✓
 * - tenant_id absent from all request bodies ✓
 * - tenantContext prop NOT present ✓
 * - OI-USEAUTH-PATH: ../../auth/AuthContext.jsx — unverified, remains open.
 * - OI-TENANTADMIN-STAGE12: GET /users, GET /tenant-config, POST /users/invite all blocked.
 * M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx'; // OI-USEAUTH-PATH: verify ui/src/auth/useAuth exists

const API_BASE = '/api/v1';

function normalizeResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function getErrorMessage(error) {
  return error?.message || 'Something went wrong.';
}

export default function CompAdminUserCreation({
  tenantId,
  tier,
  onBack,
  onContinue
}) {
  const { token } = useAuth();

  const [users, setUsers] = useState([]);
  const [inviteForm, setInviteForm] = useState({ email: '', fullName: '' });
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [seatUsed, setSeatUsed] = useState(0);
  const [seatLimit, setSeatLimit] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState(null);

  const seatGateReached = useMemo(
    () => Number(seatLimit) > 0 && Number(seatUsed) >= Number(seatLimit),
    [seatLimit, seatUsed]
  );

  const inviteDisabled = useMemo(
    () =>
      seatGateReached ||
      isSending ||
      !inviteForm.email.trim() ||
      !inviteForm.fullName.trim(),
    [inviteForm.email, inviteForm.fullName, isSending, seatGateReached]
  );

  const fetchUsersAndConfig = useCallback(async () => {
    setLoadError(null);

    try {
      // OI-TENANTADMIN-STAGE12: GET /users endpoint contract goes here
      const usersResponse = await fetch(`${API_BASE}/users`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!usersResponse.ok) {
        const payload = await usersResponse.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Failed to load users');
      }

      const usersPayload = await usersResponse.json();
      const normalizedUsers = normalizeResponse(usersPayload);
      setUsers(normalizedUsers);

      // OI-TENANTADMIN-STAGE12: GET /tenant-config endpoint contract goes here
      // OI-CONF03-TENANT-CONFIG-RBAC: requires tenant_super_admin — comp_admin gets 403.
      // Gracefully degrade to unlimited seats on 403 rather than blocking the panel.
      const configResponse = await fetch(`${API_BASE}/tenant-config`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
      let config = {};
      if (configResponse.ok) {
        const configPayload = await configResponse.json();
        config = configPayload?.data || configPayload || {};
      } else if (configResponse.status !== 403) {
        const payload = await configResponse.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Failed to load tenant configuration');
      }

      const limit =
        config.max_comp_admins ||
        config.maxCompAdmins ||
        config.seat_limit ||
        config.seatLimit ||
        0;

      const activeCompAdmins = normalizedUsers.filter(
        (user) =>
          user.is_active !== false &&
          (user.user_type === 'comp_admin' ||
            user.userType === 'comp_admin' ||
            user.role_name === 'comp_admin')
      );

      setSeatLimit(Number(limit));
      setSeatUsed(
        Number(config.comp_admin_count || config.compAdminCount || activeCompAdmins.length)
      );
    } catch (error) {
      setLoadError(getErrorMessage(error));
    }
  }, [token]);

  useEffect(() => {
    fetchUsersAndConfig();
  }, [fetchUsersAndConfig]);

  useEffect(() => {
    if (!toast) return undefined;

    const timeoutId = window.setTimeout(() => {
      setToast(null);
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [toast]);

  const updateInviteForm = (field, value) => {
    setInviteForm((current) => ({ ...current, [field]: value }));
  };

  const sendInvite = async (event) => {
    event.preventDefault();
    if (inviteDisabled) return;

    setIsSending(true);
    setSendError(null);

    try {
      // OI-TENANTADMIN-STAGE12: POST /users/invite request/response contract goes here
      const response = await fetch(`${API_BASE}/users/invite`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: inviteForm.email.trim(),
          full_name: inviteForm.fullName.trim(),
          user_type: 'comp_admin'
          // tenant_id intentionally omitted — extracted from JWT server-side (global convention §1.3)
        })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Invite failed');
      }

      const payload = await response.json().catch(() => null);
      const invitedUser = payload?.data || payload || {
        email: inviteForm.email.trim(),
        full_name: inviteForm.fullName.trim(),
        user_type: 'comp_admin',
        is_active: true
      };

      setUsers((current) => [invitedUser, ...current]);
      setSeatUsed((current) => Number(current) + 1);
      setInviteForm({ email: '', fullName: '' });
      setToast('Invite sent.');
    } catch (error) {
      setSendError(getErrorMessage(error));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="relative rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-sm">
      {toast && (
        <div className="absolute right-5 top-5 rounded-xl bg-[rgb(var(--color-success))] px-4 py-3 text-tok-sm font-medium text-white shadow-lg">
          {toast}
        </div>
      )}

      <div className="border-b border-[rgb(var(--color-border))] p-5">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">CONF-03</p>
        <h2 className="mt-1 text-tok-xl font-semibold text-[rgb(var(--color-text))]">
          Comp admin user creation
        </h2>
        <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Invite compensation administrators who can manage downstream setup and plan operations.
        </p>
      </div>

      <div className="grid gap-5 p-5 lg:grid-cols-[360px_1fr]">
        <form
          onSubmit={sendInvite}
          className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4"
        >
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Send invite</h3>

          <div className="mt-4 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-3">
            <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
              Seat usage
            </p>
            <p className="mt-1 text-tok-2xl font-semibold text-[rgb(var(--color-text))]">
              {seatUsed} / {seatLimit || '—'}
            </p>
            {seatGateReached && (
              <p className="mt-2 text-tok-sm text-[rgb(var(--color-danger))]">
                Seat limit reached. Invite is disabled.
              </p>
            )}
          </div>

          {loadError && (
            <div className="mt-4 rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-3 text-tok-sm text-[rgb(var(--color-danger))]">
              <p className="font-semibold">Unable to load users/config</p>
              <p className="mt-1">{loadError}</p>
              <button
                type="button"
                onClick={fetchUsersAndConfig}
                className="mt-3 rounded-lg bg-[rgb(var(--color-danger))] px-3 py-2 text-white"
              >
                Retry
              </button>
            </div>
          )}

          <label className="mt-4 block text-tok-sm font-medium text-[rgb(var(--color-text))]">
            Full name
          </label>
          <input
            value={inviteForm.fullName}
            onChange={(event) => updateInviteForm('fullName', event.target.value)}
            className="mt-2 w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm"
            placeholder="Jane Admin"
          />

          <label className="mt-4 block text-tok-sm font-medium text-[rgb(var(--color-text))]">
            Email
          </label>
          <input
            type="email"
            value={inviteForm.email}
            onChange={(event) => updateInviteForm('email', event.target.value)}
            className="mt-2 w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm"
            placeholder="jane@example.com"
          />

          {sendError && (
            <div className="mt-4 rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {sendError}
            </div>
          )}

          <button
            type="submit"
            disabled={inviteDisabled}
            className="mt-5 w-full rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:cursor-not-allowed disabled:bg-[rgb(var(--color-disabled))]"
          >
            {isSending ? 'Sending...' : 'Send Invite'}
          </button>
        </form>

        <div className="overflow-hidden rounded-xl border border-[rgb(var(--color-border))]">
          <div className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Current users</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
              <thead className="bg-[rgb(var(--color-surface))]">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Email</th>
                  <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Active</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-[rgb(var(--color-text-muted))]">
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map((user, index) => (
                    <tr key={user.user_id || user.id || user.email || index}>
                      <td className="px-4 py-3 text-[rgb(var(--color-text))]">
                        {user.full_name || user.fullName || user.name || '—'}
                      </td>
                      <td className="px-4 py-3 text-[rgb(var(--color-text))]">{user.email}</td>
                      <td className="px-4 py-3 text-[rgb(var(--color-text))]">
                        {user.user_type || user.userType || '—'}
                      </td>
                      <td className="px-4 py-3 text-[rgb(var(--color-text))]">
                        <span
                          className={`rounded-full px-2 py-1 text-tok-xs font-medium ${
                            user.is_active !== false
                              ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]'
                              : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
                          }`}
                        >
                          {user.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] p-5">
        <button
          type="button"
          onClick={onBack}
          className="rounded-xl border border-[rgb(var(--color-border))] px-5 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))]"
        >
          {tier === 'essentials' ? 'Continue to Gate Summary' : 'Continue'}
        </button>
      </div>
    </section>
  );
}
